import { useEffect, useMemo, useState } from 'react';
import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import {
  COMTRADE_FALLBACK,
  groupComtradeRegions,
  partnerValue,
  shortPartnerName,
  worldTotal,
  type ComtradeMapPayload,
  type ComtradePartner,
  type ComtradeTile,
} from '@/components/value-chain/starlinkComtrade';
import { formatGrowth, formatUsdCompact } from '@/components/value-chain/revenueMap';
import { valueChainService } from '@/services/api';

type ChartMode = 'treemap' | 'bar';

export function StarlinkComtradeMap() {
  const [payload, setPayload] = useState<ComtradeMapPayload>(COMTRADE_FALLBACK);
  const [year, setYear] = useState('2024');
  const [mode, setMode] = useState<ChartMode>('treemap');
  const [selectedIso, setSelectedIso] = useState<string | null>('MEX');

  useEffect(() => {
    let cancelled = false;
    valueChainService
      .getStarlinkTrade()
      .then((next) => {
        if (!cancelled && next?.partners?.length) setPayload(next);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const years = payload.years.length ? payload.years : ['2022', '2023', '2024'];
  const total = worldTotal(payload, year);
  const regions = useMemo(() => groupComtradeRegions(payload, year), [payload, year]);
  const selected =
    payload.partners.find((partner) => partner.iso3 === selectedIso) ?? payload.partners[0] ?? null;
  const rows = chunk(regions, 3);
  const barRows = payload.partners
    .map((partner) => ({
      name: shortPartnerName(partner.partner),
      iso3: partner.iso3,
      valueB: partnerValue(partner, year) / 1_000_000_000,
      color: tileTone(growthBetween(partner, year)).bg,
    }))
    .filter((row) => row.valueB > 0)
    .slice(0, 12);

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-[15px] font-semibold text-slate-900">US HS 851762 exports</h2>
          <p className="mt-0.5 text-[12px] text-slate-500">
            UN Comtrade · reporter 842 · destination mix for data-transmission machines. Not Starlink-only.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5 text-[11px]">
            {years.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => setYear(item)}
                className={`rounded-md px-2.5 py-1 font-medium ${
                  year === item ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'
                }`}
              >
                {item}
              </button>
            ))}
          </div>
          <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5 text-[11px]">
            {(['treemap', 'bar'] as const).map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => setMode(item)}
                className={`rounded-md px-2.5 py-1 font-medium capitalize ${
                  mode === item ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'
                }`}
              >
                {item === 'bar' ? 'Bar Chart' : 'Treemap'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {mode === 'bar' ? (
        <div className="h-[360px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={barRows} barSize={28} margin={{ top: 8, right: 8, left: 0, bottom: 48 }}>
              <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-32} textAnchor="end" />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(value) => `$${Number(value).toFixed(1)}b`} />
              <Tooltip
                formatter={(value: number) => [`$${Number(value).toFixed(2)}b`, 'US exports']}
                labelStyle={{ fontSize: 12 }}
              />
              <Bar dataKey="valueB" radius={[4, 4, 0, 0]}>
                {barRows.map((row) => (
                  <Cell key={row.iso3} fill={row.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="grid min-h-[420px] gap-3 xl:grid-cols-[minmax(0,1fr)_240px]">
          <div className="flex min-h-0 min-w-0 flex-col gap-2">
            {rows.map((row) => (
              <div key={row.map((region) => region.id).join('-')} className="flex min-h-0 flex-1 gap-2">
                {row.map((region) => (
                  <div
                    key={region.id}
                    className="min-h-[168px] min-w-0"
                    style={{ flex: Math.max(region.revenue, 1) }}
                  >
                    <RegionCard
                      label={region.label}
                      revenue={region.revenue}
                      tiles={region.tiles}
                      selectedId={selectedIso}
                      onSelect={setSelectedIso}
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>
          <aside className="space-y-3">
            <article className="rounded-xl border border-slate-100 bg-slate-50 p-3">
              <p className="text-[11px] font-medium text-slate-400">World total · {year}</p>
              <p className="mt-1 text-[26px] leading-none font-semibold tracking-tight text-slate-900">
                {formatUsdCompact(total)}
              </p>
              <p className="mt-1.5 text-[11px] text-slate-500">
                {payload.partner_count.toLocaleString()} destinations · HS {payload.hs_code}
              </p>
            </article>
            {selected ? <SelectedPartner partner={selected} year={year} total={total} /> : null}
            <p className="text-[10px] leading-relaxed text-slate-400">{payload.caveat}</p>
          </aside>
        </div>
      )}
    </section>
  );
}

function RegionCard({
  label,
  revenue,
  tiles,
  selectedId,
  onSelect,
}: {
  label: string;
  revenue: number;
  tiles: ComtradeTile[];
  selectedId: string | null;
  onSelect: (iso3: string) => void;
}) {
  return (
    <section className="flex h-full min-h-0 flex-col overflow-hidden rounded-xl bg-[#0d4f3c]">
      <header className="flex h-9 shrink-0 items-center justify-between gap-2 px-3 text-white">
        <h3 className="min-w-0 truncate text-[13px] font-medium">{label}</h3>
        <span className="shrink-0 text-[13px] font-semibold tabular-nums">{formatUsdCompact(revenue)}</span>
      </header>
      <div className="flex min-h-0 flex-1 flex-wrap content-stretch gap-1 p-1 pt-0">
        {tiles.map((tile) => {
          const tone = tileTone(tile.growthPct);
          const selected = tile.iso3 != null && tile.iso3 === selectedId;
          return (
            <button
              key={tile.id}
              type="button"
              title={`${tile.label} · ${formatUsdCompact(tile.value)}`}
              onClick={() => {
                if (tile.iso3) onSelect(tile.iso3);
              }}
              className="flex min-h-[72px] min-w-[92px] flex-1 flex-col justify-between overflow-hidden rounded-md p-2 text-left"
              style={{
                flex: Math.max(tile.value, 1),
                background: tone.bg,
                color: tone.fg,
                boxShadow: selected ? 'inset 0 0 0 2px #fff' : undefined,
              }}
            >
              <span className="line-clamp-2 text-[11px] leading-tight font-semibold">
                {tile.label}
                {tile.labeled ? (
                  <span className="ml-1 rounded bg-white/20 px-1 py-px text-[8px] font-medium tracking-wide uppercase">
                    kits
                  </span>
                ) : null}
              </span>
              <span>
                <span className="block text-[15px] leading-none font-semibold tabular-nums">
                  {formatUsdCompact(tile.value)}
                </span>
                <span className="mt-0.5 block text-[10px] opacity-80">{formatGrowth(tile.growthPct)}</span>
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

function SelectedPartner({
  partner,
  year,
  total,
}: {
  partner: ComtradePartner;
  year: string;
  total: number;
}) {
  const value = partnerValue(partner, year);
  const share = total > 0 ? (value / total) * 100 : 0;
  const change = growthBetween(partner, year);
  return (
    <article className="rounded-xl border border-slate-200 p-3">
      <p className="text-[11px] font-medium text-slate-400">Selected destination</p>
      <p className="mt-1 text-[15px] font-semibold text-slate-900">{partner.partner}</p>
      <p className="text-[11px] text-slate-500">
        {partner.region}
        {partner.labeled ? ' · labeled Starlink kits on public bills' : ''}
      </p>
      <dl className="mt-2 space-y-1 text-[12px]">
        <div className="flex justify-between gap-3">
          <dt className="text-slate-500">{year} US exports</dt>
          <dd className="font-medium text-slate-900">{formatUsdCompact(value)}</dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt className="text-slate-500">Share of HS 851762</dt>
          <dd className="font-medium text-slate-900">{share >= 10 ? share.toFixed(0) : share.toFixed(1)}%</dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt className="text-slate-500">vs 2022</dt>
          <dd className={change != null && change < 0 ? 'font-medium text-rose-600' : 'font-medium text-emerald-700'}>
            {formatGrowth(change)}
          </dd>
        </div>
      </dl>
    </article>
  );
}

function growthBetween(partner: ComtradePartner, year: string): number | null {
  const base = partnerValue(partner, '2022');
  const current = partnerValue(partner, year);
  if (base <= 0 || year === '2022') return null;
  return ((current / base) - 1) * 100;
}

function tileTone(growth: number | null): { bg: string; fg: string } {
  if (growth == null || !Number.isFinite(growth)) return { bg: '#e5e7eb', fg: '#4b5563' };
  if (growth > 20) return { bg: '#157a3a', fg: '#ffffff' };
  if (growth >= 10) return { bg: '#4ade80', fg: '#14532d' };
  if (growth >= 0) return { bg: '#bbf7d0', fg: '#14532d' };
  if (growth > -10) return { bg: '#fecdd3', fg: '#9f1239' };
  return { bg: '#f43f5e', fg: '#ffffff' };
}

function chunk<T>(items: T[], size: number): T[][] {
  const rows: T[][] = [];
  for (let i = 0; i < items.length; i += size) rows.push(items.slice(i, i + size));
  return rows;
}
